import type { PageClientSide } from '@readme/backend/models/page/types';
import type { ProjectClientSide } from '@readme/backend/models/project/types';

import React, { useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';

// `VersionContext` is only used for metrics tracking and not business logic,
// and it was a bug prone prop for consumers to have to provide
// eslint-disable-next-line no-restricted-imports
import { VersionContext } from '@core/context';
import { useMetricsAPIFetcher } from '@core/hooks/useMetricsAPI';
import usePagination from '@core/hooks/usePagination';

import DateLine from '@ui/DateLine';
import type { PageVote } from '@ui/PageThumbs';
import PageThumbs from '@ui/PageThumbs';
import PaginationControls from '@ui/PaginationControls';
import WhatsNext from '@ui/WhatsNext';

interface DocFooterProps {
  doc: PageClientSide;
  project: ProjectClientSide;
}

function DocFooter({ doc, project }: DocFooterProps) {
  const { prev, next } = usePagination<PageClientSide>(doc);
  const location = useLocation();
  const pathname = location?.pathname;

  // metrics is only available for versions and not for other branch types
  // eslint-disable-next-line readme-internal/no-use-version-context
  const { version } = useContext(VersionContext);
  const metricsFetch = useMetricsAPIFetcher({ version });

  const pageType = doc?.isReference ? 'reference' : 'docs';

  const sendToMetrics = useCallback(
    (body: PageVote) => {
      const { vote, path } = body || {};
      if (typeof vote !== 'number' || !path) return false;

      return metricsFetch({
        path: 'create/thumb',
        method: 'POST',
        body: { requestType: 'pageQuality', ...body },
      });
    },
    [metricsFetch],
  );

  const showWhatsNext = Boolean(doc?.next?.pages?.length || doc?.next?.description);

  return (
    <React.Fragment key={pathname}>
      <div className="UpdatedAt">
        <DateLine icon="icon icon-watch" prefix="Updated" tag="p" time={doc?.updatedAt} />
      </div>
      <hr className="NextStepsDivider" />
      {showWhatsNext ? (
        <WhatsNext
          description={doc.next?.description}
          label={project.appearance.nextStepsLabel}
          pages={doc.next?.pages}
        />
      ) : (
        <PaginationControls next={next} pageType={pageType} prev={prev} />
      )}
      {!!project?.metrics?.thumbsEnabled && (
        <PageThumbs pathname={pathname} projectSubdomain={project.subdomain} sendToMetrics={sendToMetrics} />
      )}
    </React.Fragment>
  );
}

export default DocFooter;
